import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  Action,
  AssistantType,
  ConstraintInterface,
  Effect,
  InvocationContextApp,
  InvocationContextAuthState,
  InvocationContextChannel,
  InvocationContextMedium,
  NamespaceType,
} from '@vendasta/ai-assistants';

// Simple assignment constraint data structure
export interface AssignmentConstraint {
  scopeType: string;
  scopeValue: number;
}

// Simple assignment constraint form controls
export type AiAssignmentConstraintFormControls = {
  scopeType: FormControl<string | null>;
  scopeValue: FormControl<number | null>;
};

export class AiAssignmentConstraintForm extends FormGroup<AiAssignmentConstraintFormControls> {
  constructor(assignmentConstraint?: AssignmentConstraint) {
    super({
      scopeType: new FormControl(assignmentConstraint?.scopeType ?? null),
      scopeValue: new FormControl(assignmentConstraint?.scopeValue ?? null),
    });
  }
}

export interface InvokeConstraint {
  effect: number;
  medium?: number;
  channels: string[];
  authState?: number;
  app?: number;
}

export type AiInvokeConstraintFormControls = {
  effect: FormControl<number>;
  medium: FormControl<number | null>;
  channels: FormControl<string[]>;
  authState: FormControl<number | null>;
  app: FormControl<number | null>;
};

export class AiInvokeConstraintForm extends FormGroup<AiInvokeConstraintFormControls> {
  constructor(invokeConstraint?: InvokeConstraint) {
    super({
      effect: new FormControl<number>(invokeConstraint?.effect ?? Effect.EFFECT_ALLOW, { nonNullable: true }),
      medium: new FormControl(invokeConstraint?.medium ?? null),
      channels: new FormControl<string[]>(invokeConstraint?.channels ?? [], { nonNullable: true }),
      authState: new FormControl(invokeConstraint?.authState ?? null),
      app: new FormControl(invokeConstraint?.app ?? null),
    });
  }
}

// Main form controls - checkbox for hiding capabilities or tools and a list of assignment constraints
export type AiConstraintsFormControls = {
  isHidden: FormControl<boolean>;
  assignmentConstraints: FormArray<AiAssignmentConstraintForm>;
  invokeConstraints: FormArray<AiInvokeConstraintForm>;
};

export class AiConstraintsForm extends FormGroup<AiConstraintsFormControls> {
  private _passthroughConstraints: ConstraintInterface[] = [];

  constructor(constraints: ConstraintInterface[] = []) {
    const { isHidden, assignmentConstraints, invokeConstraints, passthroughConstraints } =
      AiConstraintsForm.transformConstraintsToFormFields(constraints);

    super({
      isHidden: new FormControl<boolean>(isHidden, { nonNullable: true }),
      assignmentConstraints: new FormArray<AiAssignmentConstraintForm>(
        assignmentConstraints.map((constraint) => new AiAssignmentConstraintForm(constraint)),
      ),
      invokeConstraints: new FormArray<AiInvokeConstraintForm>(
        invokeConstraints.map((constraint) => new AiInvokeConstraintForm(constraint)),
      ),
    });

    this._passthroughConstraints = passthroughConstraints;
  }

  public static transformConstraintsToFormFields(constraints: ConstraintInterface[]): {
    isHidden: boolean;
    assignmentConstraints: AssignmentConstraint[];
    invokeConstraints: InvokeConstraint[];
    passthroughConstraints: ConstraintInterface[];
  } {
    const viewConstraint = constraints.find((c) => c.action === Action.ACTION_VIEW);
    const isHidden = viewConstraint?.effect === Effect.EFFECT_DENY || false;

    const assignmentConstraints: AssignmentConstraint[] = constraints
      .filter((c) => c.action === Action.ACTION_ASSIGN)
      .map((constraint) => {
        if (constraint.scope?.assistantNamespaceType !== undefined) {
          return {
            scopeType: 'assistantNamespaceType',
            scopeValue: constraint.scope.assistantNamespaceType,
          };
        } else if (constraint.scope?.assistantType !== undefined) {
          return {
            scopeType: 'assistantType',
            scopeValue: constraint.scope.assistantType,
          };
        }
        return { scopeType: 'assistantType', scopeValue: 1 };
      });

    const channelNames = CONSTRAINT_CONFIG.channels;
    const invokeConstraints: InvokeConstraint[] = [];
    const invokeGroupMap = new Map<string, InvokeConstraint>();

    constraints
      .filter((c) => c.action === Action.ACTION_INVOKE)
      .forEach((constraint) => {
        const effect = constraint.effect ?? Effect.EFFECT_ALLOW;
        const medium = constraint.scope?.invocationContext?.medium ?? null;
        const channelEnum = constraint.scope?.invocationContext?.channel ?? null;
        const channelName = channelEnum != null ? channelNames.get(channelEnum) : null;
        const authState = constraint.scope?.invocationContext?.authState ?? null;
        const app = constraint.scope?.invocationContext?.app ?? null;

        const groupKey = `${effect}|${medium}|${authState}|${app}`;
        const existing = invokeGroupMap.get(groupKey);

        if (existing) {
          if (channelName) existing.channels.push(channelName);
        } else {
          const row: InvokeConstraint = {
            effect,
            medium,
            channels: channelName ? [channelName] : [],
            authState,
            app,
          };
          invokeGroupMap.set(groupKey, row);
          invokeConstraints.push(row);
        }
      });

    const knownActions = new Set([Action.ACTION_VIEW, Action.ACTION_ASSIGN, Action.ACTION_INVOKE]);
    const passthroughConstraints = constraints.filter((c) => !knownActions.has(c.action));

    return { isHidden, assignmentConstraints, invokeConstraints, passthroughConstraints };
  }

  toConstraints(): ConstraintInterface[] {
    const constraints: ConstraintInterface[] = [];
    const formValue = this.getRawValue();

    if (formValue.isHidden) {
      constraints.push({
        action: Action.ACTION_VIEW,
        effect: Effect.EFFECT_DENY,
        scope: undefined,
      });
    }

    formValue.assignmentConstraints?.forEach((assignmentForm) => {
      if (assignmentForm.scopeType && assignmentForm.scopeValue !== null) {
        constraints.push({
          action: Action.ACTION_ASSIGN,
          effect: Effect.EFFECT_ALLOW,
          scope: { [assignmentForm.scopeType]: assignmentForm.scopeValue },
        });
      }
    });

    const channelNameToEnum = new Map<string, number>();
    CONSTRAINT_CONFIG.channels.forEach((name, enumVal) => channelNameToEnum.set(name, enumVal));

    formValue.invokeConstraints?.forEach((invokeForm) => {
      const baseContext: Record<string, number> = {};
      if (invokeForm.medium != null) baseContext['medium'] = invokeForm.medium;
      if (invokeForm.authState != null) baseContext['authState'] = invokeForm.authState;
      if (invokeForm.app != null) baseContext['app'] = invokeForm.app;

      const channelNames = invokeForm.channels ?? [];
      if (channelNames.length === 0) {
        constraints.push({
          action: Action.ACTION_INVOKE,
          effect: invokeForm.effect,
          scope: Object.keys(baseContext).length > 0 ? { invocationContext: { ...baseContext } } : undefined,
        });
      } else {
        channelNames.forEach((name) => {
          const channel = channelNameToEnum.get(name);
          if (channel != null) {
            constraints.push({
              action: Action.ACTION_INVOKE,
              effect: invokeForm.effect,
              scope: { invocationContext: { ...baseContext, channel } },
            });
          }
        });
      }
    });

    constraints.push(...this._passthroughConstraints);

    return constraints;
  }

  addAssignmentConstraint(): void {
    const constraintsArray = this.controls.assignmentConstraints;
    constraintsArray.push(new AiAssignmentConstraintForm());
    this.markAsDirty();
  }

  removeAssignmentConstraint(index: number): void {
    const constraintsArray = this.controls.assignmentConstraints;
    if (index >= 0 && index < constraintsArray.length) {
      constraintsArray.removeAt(index);
      this.markAsDirty();
    }
  }

  addInvokeConstraint(): void {
    const constraintsArray = this.controls.invokeConstraints;
    constraintsArray.push(new AiInvokeConstraintForm());
    this.markAsDirty();
  }

  removeInvokeConstraint(index: number): void {
    const constraintsArray = this.controls.invokeConstraints;
    if (index >= 0 && index < constraintsArray.length) {
      constraintsArray.removeAt(index);
      this.markAsDirty();
    }
  }
}

export const CONSTRAINT_CONFIG = {
  actions: new Map<number, string>([
    [Action.ACTION_ASSIGN, 'Assign'],
    [Action.ACTION_VIEW, 'View'],
    [Action.ACTION_INVOKE, 'Invoke'],
  ]),

  effects: new Map<number, string>([
    [Effect.EFFECT_ALLOW, 'Allow'],
    [Effect.EFFECT_DENY, 'Deny'],
  ]),

  assistantNamespaceTypes: new Map<number, string>([
    [NamespaceType.NAMESPACE_TYPE_PARTNER, 'Partner'],
    [NamespaceType.NAMESPACE_TYPE_ACCOUNT_GROUP, 'Account Group'],
  ]),

  assistantTypes: new Map<number, string>([
    [AssistantType.ASSISTANT_TYPE_SYSTEM, 'System'],
    [AssistantType.ASSISTANT_TYPE_INBOX, 'Chat Receptionist'],
    [AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING, 'Social Marketing'],
    [AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST, 'Voice Receptionist'],
    [AssistantType.ASSISTANT_TYPE_CUSTOM, 'Custom'],
    [AssistantType.ASSISTANT_TYPE_SALES_COACH, 'Sales Coach'],
    [AssistantType.ASSISTANT_TYPE_REPUTATION_MANAGEMENT_REVIEW_AGENT, 'Review Manager'],
    [AssistantType.ASSISTANT_TYPE_AEO_AND_SEO_EXPERT, 'AI Search Expert'],
  ]),

  mediums: new Map<number, string>([
    [InvocationContextMedium.TEXT, 'Text'],
    [InvocationContextMedium.VOICE, 'Voice'],
  ]),

  channels: new Map<number, string>([
    [InvocationContextChannel.SMS, 'SMS'],
    [InvocationContextChannel.WEBCHAT, 'Webchat'],
    [InvocationContextChannel.PHONE, 'Phone'],
    [InvocationContextChannel.WHATSAPP, 'WhatsApp'],
    [InvocationContextChannel.FACEBOOK, 'Facebook'],
    [InvocationContextChannel.INSTAGRAM, 'Instagram'],
    [InvocationContextChannel.EMAIL, 'Email'],
    [InvocationContextChannel.PLATFORM, 'Platform'],
    [InvocationContextChannel.SNAPSHOT, 'Snapshot'],
    [InvocationContextChannel.SYSTEM, 'System'],
  ]),

  authStates: new Map<number, string>([
    [InvocationContextAuthState.ANONYMOUS, 'Anonymous'],
    [InvocationContextAuthState.AUTHENTICATED, 'Authenticated'],
  ]),

  apps: new Map<number, string>([
    [InvocationContextApp.BUSINESS_CENTER, 'Business Center'],
    [InvocationContextApp.PARTNER_CENTER, 'Partner Center'],
  ]),

  scopeTypes: [
    { value: 'none', label: 'For All' },
    { value: 'assistantNamespaceType', label: 'For Assistant Namespace' },
    { value: 'assistantType', label: 'For Assistant Type' },
  ],
};
